import { bindMarketoFormAnalytics } from './marketo-form-analytics';
import { bindMarketoFormErrorDisplay } from './marketo-form-error-display';
import { bindMarketoFormFocusRings } from './marketo-form-focus-rings';
import { showThankYouMessage } from './marketo-thank-you';
import {
  DEFAULT_MARKETO_BASE_URL,
  DEFAULT_MARKETO_SCRIPT_URL,
  loadMarketoScript
} from './shared-marketo-forms2';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])'
].join(',');

const CLOSE_TRANSITION_MS = 180;
const GATE_STORAGE_PREFIX = 'marketo-modal-shown:';

let activeModal: HTMLElement | null = null;
let activeTrigger: HTMLElement | null = null;
let closeTimeout: number | undefined;

function getFocusableElements(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    element => element.offsetParent !== null || element === document.activeElement
  );
}

function setPageScrollLocked(locked: boolean): void {
  document.documentElement.classList.toggle('marketo-form-modal-page-locked', locked);
}

function getCloseTransitionMs(): number {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return 0;

  return CLOSE_TRANSITION_MS;
}

// A modal can gate an ordinary HTML form: the trigger opens the modal instead of
// submitting, and the form is submitted once the Marketo form succeeds.
function getBlockedForm(modal: HTMLElement): HTMLFormElement | null {
  const formId = modal.dataset.marketoBlockingHtmlFormId?.trim();
  if (!formId) return null;

  const form = document.getElementById(formId);
  return form instanceof HTMLFormElement ? form : null;
}

function submitBlockedForm(modal: HTMLElement): void {
  const form = getBlockedForm(modal);
  if (!form) return;

  // requestSubmit keeps native validation and submit events intact.
  if (typeof form.requestSubmit === 'function') {
    form.requestSubmit();
  } else {
    form.submit();
  }
}

function gateStorageKey(modal: HTMLElement): string {
  return `${GATE_STORAGE_PREFIX}${modal.id}`;
}

function hasSeenGate(modal: HTMLElement): boolean {
  try {
    return window.sessionStorage.getItem(gateStorageKey(modal)) === 'true';
  } catch {
    // Private-mode or blocked storage: fall back to always showing the modal.
    return false;
  }
}

function markGateSeen(modal: HTMLElement): void {
  try {
    window.sessionStorage.setItem(gateStorageKey(modal), 'true');
  } catch {
    // Non-fatal: the gate simply shows again next time.
  }
}

function setStatus(modal: HTMLElement, message: string, isError = false): void {
  const status = modal.querySelector<HTMLElement>('[data-marketo-form-status]');
  if (!status) return;

  status.textContent = message;
  status.dataset.state = isError ? 'error' : 'loading';
}

async function loadMarketoForm(modal: HTMLElement): Promise<void> {
  if (modal.dataset.marketoFormLoaded === 'true') return;
  if (modal.dataset.marketoFormLoading === 'true') return;

  const formId = Number(modal.dataset.marketoFormId);
  const munchkinId = modal.dataset.marketoMunchkinId?.trim();
  const baseUrl = modal.dataset.marketoBaseUrl?.trim() || DEFAULT_MARKETO_BASE_URL;
  const scriptUrl =
    modal.dataset.marketoScriptUrl?.trim() || `${baseUrl}/js/forms2/js/forms2.min.js`;

  if (!formId || !munchkinId) {
    setStatus(
      modal,
      modal.dataset.marketoConfigErrorLabel ?? 'Form configuration is missing.',
      true
    );
    return;
  }

  try {
    modal.dataset.marketoFormLoading = 'true';
    await loadMarketoScript(scriptUrl || DEFAULT_MARKETO_SCRIPT_URL);

    if (!window.MktoForms2) {
      throw new Error('Marketo Forms2 API unavailable');
    }

    window.MktoForms2.loadForm(baseUrl, munchkinId, formId, form => {
      modal.dataset.marketoFormLoaded = 'true';
      modal.dataset.marketoFormLoading = 'false';
      const status = modal.querySelector<HTMLElement>('[data-marketo-form-status]');
      status?.setAttribute('hidden', '');

      if (form) {
        bindMarketoFormErrorDisplay(form, modal);
        bindMarketoFormFocusRings(form, modal);
        bindMarketoFormAnalytics(form, modal, {
          // Reclaim success so a Marketo/GTM follow-up redirect cannot beat the calculator submit.
          ownSuccessHandlers:
            Boolean(modal.querySelector('[data-marketo-thank-you]')) ||
            Boolean(getBlockedForm(modal)),
          onSuccess: () => {
            // Gate flow: mark seen on success, then submit the blocked HTML form.
            if (getBlockedForm(modal)) {
              markGateSeen(modal);
              closeModal(modal);
              submitBlockedForm(modal);
              return false;
            }

            // CMS thank-you present → swap in place and block Marketo redirect.
            // No template → leave undefined so Marketo keeps default follow-up.
            if (showThankYouMessage(modal)) {
              return false;
            }

            return undefined;
          }
        });
      }
    });
  } catch (error) {
    console.error('[marketo-form-modal] Unable to load form', error);
    modal.dataset.marketoFormLoading = 'false';
    setStatus(
      modal,
      modal.dataset.marketoLoadErrorLabel ??
        'The form could not be loaded. Please try again later.',
      true
    );
  }
}

function closeModal(modal: HTMLElement): void {
  modal.removeEventListener('keydown', handleModalKeydown);

  if (activeModal === modal) {
    activeModal = null;
  }

  setPageScrollLocked(false);

  const trigger = activeTrigger;
  activeTrigger = null;
  modal.dataset.state = 'closing';

  window.clearTimeout(closeTimeout);
  closeTimeout = window.setTimeout(() => {
    modal.setAttribute('hidden', '');
    modal.dataset.state = 'closed';
    trigger?.focus();
  }, getCloseTransitionMs());
}

function openModal(modal: HTMLElement, trigger: HTMLElement): void {
  if (activeModal && activeModal !== modal) {
    closeModal(activeModal);
  }

  activeModal = modal;
  activeTrigger = trigger;
  window.clearTimeout(closeTimeout);
  modal.removeAttribute('hidden');
  modal.dataset.state = 'opening';
  setPageScrollLocked(true);

  requestAnimationFrame(() => {
    modal.dataset.state = 'open';
  });

  const dialog = modal.querySelector<HTMLElement>('[data-marketo-modal-dialog]');
  const heading = modal.querySelector<HTMLElement>('[data-marketo-modal-heading]');
  requestAnimationFrame(() => {
    (dialog ?? heading ?? modal).focus();
  });

  modal.addEventListener('keydown', handleModalKeydown);
  void loadMarketoForm(modal);
}

function handleModalKeydown(event: KeyboardEvent): void {
  const modal = event.currentTarget as HTMLElement;

  if (event.key === 'Escape') {
    event.preventDefault();
    closeModal(modal);
    return;
  }

  if (event.key !== 'Tab') return;

  const dialog = modal.querySelector<HTMLElement>('[data-marketo-modal-dialog]');
  if (!dialog) return;

  const focusableElements = getFocusableElements(dialog);
  if (focusableElements.length === 0) {
    event.preventDefault();
    dialog.focus();
    return;
  }

  const firstElement = focusableElements[0];
  const lastElement = focusableElements.at(-1);

  if (!firstElement || !lastElement) return;

  if (event.shiftKey && document.activeElement === firstElement) {
    event.preventDefault();
    lastElement.focus();
  } else if (!event.shiftKey && document.activeElement === lastElement) {
    event.preventDefault();
    firstElement.focus();
  }
}

function getTargetModal(trigger: HTMLElement): HTMLElement | null {
  const targetId = trigger.dataset.targetModalId;
  if (!targetId) return null;

  const escapedTargetId = CSS.escape(targetId);
  return document.querySelector<HTMLElement>(
    `#${escapedTargetId}[data-module="marketo-form-modal"]`
  );
}

export default function marketoFormModal(root: HTMLElement): void {
  root.querySelectorAll<HTMLElement>('[data-marketo-modal-close]').forEach(closeTrigger => {
    closeTrigger.addEventListener('click', () => closeModal(root));
  });

  const rootId = root.id;
  if (!rootId) return;

  document.querySelectorAll<HTMLElement>('[data-marketo-modal-trigger]').forEach(trigger => {
    if (trigger.dataset.targetModalId !== rootId) return;

    trigger.addEventListener('click', () => {
      const modal = getTargetModal(trigger);
      if (!modal) return;

      // Skip the gate only after a successful Marketo submit (same session).
      if (getBlockedForm(modal) && hasSeenGate(modal)) {
        submitBlockedForm(modal);
        return;
      }

      openModal(modal, trigger);
    });
  });
}
